import { useEffect, useRef, useState } from "react";

const UPDATE_INTERVAL = 5000;

/** Formats an timestamp into a opinionated human-readable string. */
function formatTimestamp(timestampDate: Date | null): { formatted: string; needsUpdates: boolean } {
    // Handle null or invalid dates
    if (!timestampDate || isNaN(timestampDate.getTime())) return { formatted: "", needsUpdates: false };

    const nowTimestamp = new Date().setMilliseconds(0);
    const dateTimestamp = new Date(timestampDate).setMilliseconds(0);

    // Calculate time differences in seconds and minutes
    const secondsDiff = Math.floor((nowTimestamp - dateTimestamp) / 1000);
    const minuteDiff = Math.floor(secondsDiff / 60);

    if (secondsDiff < 60) return { formatted: "Just now", needsUpdates: true };
    if (minuteDiff < 60) return { formatted: `${minuteDiff} min ago`, needsUpdates: true };

    // ? Note: We don't handle timestamps older than 24 hours because the system doesn't maintain chats beyond 24 hours

    // Static time for dates older than 60 minutes - these don't need updates
    return {
        formatted: timestampDate
            .toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            })
            .toLowerCase(),
        needsUpdates: false,
    };
}

/** Create a Unix timestamp with second-level precision (no milliseconds) */
function getSecondPrecisionUnixTime(date: Date): string {
    // Clone the date first to avoid modifying the original
    return new Date(date).setMilliseconds(0).toString();
}

/** Shared state for timestamp formatting across all component instances */
const sharedState = {
    formattedCache: new Map<string, string>(),
    keyListeners: new Map<string, Set<(formatted: string) => void>>(),
    updatableKeys: new Set<string>(),

    /** Get total number of listeners across all keys */
    get listenerCount(): number {
        let count = 0;
        this.keyListeners.forEach((listeners) => {
            count += listeners.size;
        });
        return count;
    },

    subscribe(key: string, callback: (formatted: string) => void): () => void {
        if (!this.keyListeners.has(key)) {
            this.keyListeners.set(key, new Set());
        }

        this.keyListeners.get(key)?.add(callback);

        return () => {
            const listeners = this.keyListeners.get(key);
            if (listeners) {
                listeners.delete(callback);

                if (listeners.size === 0) {
                    this.keyListeners.delete(key);
                    this.formattedCache.delete(key);
                    this.updatableKeys.delete(key);
                }
            }
        };
    },
    getFormattedTimestamp(inputDate: Date | null): { formatted: string; needsUpdates: boolean; roundedKey: string } {
        if (!inputDate || isNaN(inputDate.getTime())) return { formatted: "", needsUpdates: false, roundedKey: "" };

        // Get the formatted timestamp and check if it needs updates
        const { formatted, needsUpdates } = formatTimestamp(inputDate);

        // For timestamps that don't need updates, just return the formatted time
        if (!needsUpdates) {
            return { formatted, needsUpdates, roundedKey: "" };
        }

        const roundedKey = getSecondPrecisionUnixTime(inputDate);

        // !critical for scenario where all chats end and a new one arrives later
        this.updatableKeys.add(roundedKey);

        if (!this.formattedCache.has(roundedKey)) {
            this.formattedCache.set(roundedKey, formatted);
        }

        return {
            formatted: this.formattedCache.get(roundedKey) || formatted,
            needsUpdates,
            roundedKey,
        };
    },

    updateTimestamps(): void {
        // Skip updates if there are no keys that need updating
        if (this.updatableKeys.size === 0 || this.listenerCount === 0) return;

        const keysToRemove: string[] = [];

        Array.from(this.updatableKeys).forEach((key) => {
            // Convert the timestamp string back to a Date for formatting
            const timestamp = parseInt(key, 10);
            const date = new Date(timestamp);

            const oldValue = this.formattedCache.get(key);
            const { formatted, needsUpdates } = formatTimestamp(date);

            // Only notify listeners if the value has changed
            if (oldValue !== formatted) {
                this.formattedCache.set(key, formatted);

                // Only notify listeners interested in this specific key
                const keyListenersSet = this.keyListeners.get(key);
                if (keyListenersSet) {
                    keyListenersSet.forEach((listener) => {
                        listener(formatted);
                    });
                }
            }

            // Track keys that no longer need updates
            if (!needsUpdates) {
                keysToRemove.push(key);
            }
        });

        // Remove keys that no longer need updates
        keysToRemove.forEach((key) => {
            this.updatableKeys.delete(key);
        });
    },
};

// Track shared interval reference across hook instances
let sharedIntervalId: number | null = null;

/** Helper for unit testing purposes only */
export const __sharedStateForTesting = {
    updateTimestamps: () => sharedState.updateTimestamps(),
    getUpdatableKeysSize: () => sharedState.updatableKeys.size,
    getListenersSize: () => sharedState.listenerCount,
    getInstanceCount: () => sharedState.listenerCount,
    getIntervalStatus: () => sharedIntervalId !== null,
    getCacheSize: () => sharedState.formattedCache.size,
    clearAll: () => {
        sharedState.formattedCache.clear();
        sharedState.keyListeners.clear();
        sharedState.updatableKeys.clear();
        if (sharedIntervalId !== null) {
            window.clearInterval(sharedIntervalId);
            sharedIntervalId = null;
        }
    },
};

/**
 * Format a timestamp that updates automatically when recent (< 60 minutes).
 *
 * Uses a single shared interval across all hook instances to optimize performance.
 * The interval starts when the first instance mounts and stops when the last unmounts.
 */
export function useFormattedTimestamp(messageTimestamp: Date | null): string {
    const [formattedValue, setFormattedValue] = useState<string>("");
    const keyRef = useRef<string>("");

    useEffect(() => {
        // Start the interval if it doesn't exist and this is the first hook instance
        if (sharedIntervalId === null) {
            sharedIntervalId = window.setInterval(() => {
                sharedState.updateTimestamps();
            }, UPDATE_INTERVAL);
        }

        return () => {
            // If this is the last hook instance, clear the interval and all state
            if (sharedState.listenerCount <= 1 && sharedIntervalId !== null) {
                window.clearInterval(sharedIntervalId);
                sharedIntervalId = null;
                sharedState.updatableKeys.clear();
                sharedState.formattedCache.clear();
                sharedState.keyListeners.clear();
            }
        };
    }, []);

    useEffect(() => {
        if (!messageTimestamp) {
            setFormattedValue("");
            return;
        }

        // Get formatted timestamp and metadata in one call
        const result = sharedState.getFormattedTimestamp(messageTimestamp);

        // For static timestamps (older than 60 minutes), just set the value once
        if (!result.needsUpdates) {
            setFormattedValue(result.formatted);
            return;
        }

        // For dynamic timestamps, subscribe to updates
        keyRef.current = result.roundedKey;
        setFormattedValue(result.formatted);

        // Subscribe to updates for this specific key
        return sharedState.subscribe(result.roundedKey, (newFormattedValue) => {
            setFormattedValue(newFormattedValue);
        });
    }, [messageTimestamp]);

    return formattedValue;
}
